import { searchKeysPrices } from "~/app/tickets/[eventId]/_components/Filter/constants";
import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";

export type TSetSearchParams<F extends string> = (
  data: Partial<Record<F, string>>,
) => void;

export type TUseSearchFilters<F extends string> = [
  Record<F, string | null>,
  TSetSearchParams<F>,
  () => void,
];

export function useCustomSearchParams<F extends string>(
  keys: readonly F[],
): TUseSearchFilters<F> {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentSearchParams = useMemo(() => {
    const params = new URLSearchParams(searchParams.toString());

    return keys.reduce<Record<F, string | null>>(
      (acc, key) => {
        acc[key] = params.get(key);
        return acc;
      },
      {} as Record<F, string | null>,
    );
  }, [searchParams, keys]);

  const setSearchParams: TSetSearchParams<F> = (
    data: Partial<Record<F, string>>,
  ) => {
    const newQuery: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...data,
    };

    const cleanedQuery: Record<string, string> = Object.fromEntries(
      Object.entries(newQuery).filter(
        ([_, value]) => value !== null && value !== "",
      ) as [string, string][],
    );

    window.history.replaceState(
      null,
      "",
      pathname + "?" + new URLSearchParams(cleanedQuery).toString(),
    );
  };

  const clearSearchParams = () => {
    window.history.replaceState(null, "", pathname);
  };

  return [currentSearchParams, setSearchParams, clearSearchParams];
}

export function usePricesSearchParams(): TUseSearchFilters<
  (typeof searchKeysPrices)[number]
> {
  return useCustomSearchParams(searchKeysPrices);
}
